import { useId, useState } from 'react';
import {
  Button, Checkbox, Input, Modal, ModalActions, ModalFootNote, Switch,
} from '../../../components/shared';
import { ItemToggleList } from '../../../components/shared/ItemToggleList';
import { PickPreview } from '../../../components/shared/PickPreview';
import { Segmented } from '../../../components/shared/Segmented';
import {
  useDashboardStore, type BoardWidget, type WidgetOptionValue, type WidgetSettings, type WidgetSpan,
} from '../../../store/dashboardStore';
import { useMenuEnabledFn } from '../../../lib/menu-gate';
import { getWidgetDef } from './widget-registry';
import { WidgetChromeContext, type WidgetHeight } from './widget-chrome';
import { WidgetActionsContext, type WidgetActions } from './widget-context';
import { WidgetOwnSettings, hasOwnSettings } from './WidgetOwnSettings';
import { QuickLinkOrderField } from './QuickLinkOrderField';
import {
  getItemSchema, moveItem, resolveChoices, resolveItems, resolveOption, setItemOn, setOption,
  ITEMS_RESET_PATCH, type ItemContext, type OptionDef, type WidgetItemSchema,
} from './widget-items';
import {
  draftChangeCount, draftChangeSummary, sameDraft, type WidgetDraft,
} from './widget-settings-summary';
import './widget-settings-v2.css';

/**
 * 위젯 설정 — 시안 v2 (2026-09-15 · 대시보드 시안 v2 V2 · 깃발 `VITE_DASHBOARD_V2`).
 *
 * 캡처: `_workspace/design-dashboard-v2/widget-settings-*.png` (재촬영판 — 왼쪽 3묶음).
 * 깃발이 꺼져 있으면 `WidgetGrid` 는 종전 `WidgetSettingsModal` 을 연다(이 파일은 쓰이지 않는다).
 *
 * ★★★ **왼쪽은 «이 대시보드의 위젯»** — 창 하나에서 여러 위젯을 오가며 고치고 한 번에 저장한다.
 *     위젯마다 초안을 따로 들고 있어, 다른 위젯으로 갔다 와도 고친 것이 남는다. 저장은 **바뀐 위젯만** 쓴다.
 * ★★★ **보이는 항목 · 선택지의 규칙은 전부 V1 모듈(`widget-items.ts`)이 정한다** — 이 창은 그리기만 한다.
 *     막힘 재료: 메뉴 켜짐은 지금 읽는다 · 범위(DR6) · 지각 판정(DR11) · 서버 기능은 **아직 없어 비운다**
 *     → 팀 · 전사는 이유와 함께 막히고(W-Q2), 부서는 안 보인다(Q-8).
 * ★★ 오른쪽은 **실제 위젯**이다(행동은 빈 함수 · `PickPreview` 가 inert). 이름 · 높이 · 제목은 바로 보인다.
 *    보이는 항목 · 선택지를 위젯이 읽는 것은 V3 라 지금은 머리 한 줄이 바뀐 것을 말한다(관제 결정 「V2 먼저」).
 * ★★ 기본값은 «지금 그 위젯이 그리는 것»(V1 규칙 D1) — 한 번도 안 건드린 위젯은 「기본값 그대로」다.
 * ★ 저장하지 않고 닫으면 묻는다(규칙 D14) · 발에 「이 브라우저에 저장됩니다」(규칙 D15 — 서버 저장 DR1 전).
 * ★ 위젯 고유 설정(사진 폴더 · 바로가기 · 회의실 분류)은 종전 부품을 그대로 쓴다.
 */

const SPAN_OPTIONS = [
  { value: '4', label: '1/3' }, { value: '6', label: '1/2' }, { value: '8', label: '2/3' }, { value: '12', label: '전체' },
];
const HEIGHT_OPTIONS = [{ value: 's', label: '낮게' }, { value: 'm', label: '보통' }, { value: 'l', label: '크게' }];
const SPAN_LABEL: Record<number, string> = { 4: '1/3', 6: '1/2', 8: '2/3', 12: '전체' };

/** 미리보기 안의 행동 — 아무 데도 못 간다. */
const INERT: WidgetActions = { go: () => {}, openAttendanceRequest: () => {}, openSettings: () => {} };

type LeftGroup = 'items' | 'links' | 'other';
const LEFT: Array<{ key: LeftGroup; label: string; note?: string }> = [
  { key: 'items', label: '보이는 항목을 고치는 위젯 · 1단계 8종' },
  { key: 'links', label: '바로가기 고르기 · 지금 있는 기능' },
  { key: 'other', label: '그 밖의 위젯', note: '근태 · 휴가 · 공지사항 · 주간 타임라인 · 내 태스크 등은 이름 · 폭 · 높이만 고칩니다' },
];

function groupOf(w: BoardWidget): LeftGroup {
  if (getItemSchema(w.widgetKey)) return 'items';
  if (w.widgetKey === 'quick-launch') return 'links';
  return 'other';
}

function draftOf(w: BoardWidget): WidgetDraft {
  return {
    title: w.settings?.title ?? '',
    showTitle: w.settings?.showTitle ?? true,
    height: w.settings?.height ?? 'm',
    span: w.span,
    settings: w.settings ?? {},
  };
}

interface Props {
  boardId: string;
  placed: BoardWidget;
  onClose: () => void;
}

export function WidgetSettingsV2Modal({ boardId, placed, onClose }: Props) {
  const uid = useId();
  const board = useDashboardStore((s) => s.boards.find((b) => b.id === boardId));
  const setWidgetSettings = useDashboardStore((s) => s.setWidgetSettings);
  const setWidgetSpan = useDashboardStore((s) => s.setWidgetSpan);
  const menuEnabled = useMenuEnabledFn();
  // 범위(DR6) · 지각 판정(DR11) · 서버 기능은 서버가 아직 안 준다 — 비워 두면 V1 모듈이 막는다.
  const ctx: ItemContext = { menuEnabled };

  const widgets = board?.widgets ?? [placed];
  const [initial] = useState(() => new Map(widgets.map((w) => [w.id, draftOf(w)])));
  const [drafts, setDrafts] = useState(() => new Map(initial));
  const [selectedId, setSelectedId] = useState(placed.id);

  const selected = widgets.find((w) => w.id === selectedId) ?? placed;
  const def = getWidgetDef(selected.widgetKey);
  const defaultSpan = def?.defaultSpan ?? 4;
  const schema = getItemSchema(selected.widgetKey);
  const draft = drafts.get(selected.id) ?? draftOf(selected);

  const patch = (p: Partial<WidgetDraft>) =>
    setDrafts((m) => new Map(m).set(selected.id, { ...(m.get(selected.id) ?? draft), ...p }));
  const patchSettings = (p: WidgetSettings | null) => {
    if (!p) return;
    setDrafts((m) => {
      const cur = m.get(selected.id) ?? draft;
      return new Map(m).set(selected.id, { ...cur, settings: { ...cur.settings, ...p } });
    });
  };

  const dirtyIds = widgets
    .filter((w) => {
      const d = drafts.get(w.id);
      const i = initial.get(w.id);
      return d && i && !sameDraft(d, i);
    })
    .map((w) => w.id);
  const dirty = dirtyIds.length > 0;

  const close = () => {
    if (dirty && !window.confirm('바꾼 설정을 저장하지 않고 닫을까요?')) return;
    onClose();
  };

  const save = () => {
    for (const id of dirtyIds) {
      const d = drafts.get(id)!;
      const w = widgets.find((x) => x.id === id)!;
      setWidgetSettings(boardId, id, { ...d.settings, title: d.title.trim() || undefined, showTitle: d.showTitle, height: d.height });
      if (d.span !== w.span) setWidgetSpan(boardId, id, d.span);
    }
    onClose();
  };

  const resetSelected = () => patch({
    title: '',
    showTitle: true,
    height: 'm',
    span: defaultSpan as WidgetSpan,
    // 「이 위젯 기본값으로」가 절반만 듣지 않게 — 고유 설정도 비운다(종전 창과 같다).
    settings: {
      ...draft.settings, ...ITEMS_RESET_PATCH,
      photoFolderId: undefined, photoFolderName: undefined, quickLinks: undefined, resourceCategory: undefined,
    },
  });

  const list = (
    <div className="wsv2-list">
      {LEFT.map((g) => {
        const rows = widgets.filter((w) => groupOf(w) === g.key);
        if (rows.length === 0 && !g.note) return null;
        const capId = `${uid}-cap-${g.key}`;
        return (
          <div key={g.key} className="wsv2-group">
            <p id={capId} className="wsv2-cap">{g.label}</p>
            {g.note && <p className="wsv2-note">{g.note}</p>}
            {rows.length > 0 && (
              <ul className="wsv2-rows" aria-labelledby={capId}>
                {rows.map((w) => {
                  const d = drafts.get(w.id) ?? draftOf(w);
                  const wdef = getWidgetDef(w.widgetKey);
                  const n = draftChangeCount(getItemSchema(w.widgetKey), wdef?.defaultSpan ?? 4, d);
                  return (
                    <li key={w.id}>
                      <button
                        type="button"
                        className={`wsv2-row${w.id === selected.id ? ' is-on' : ''}`}
                        aria-current={w.id === selected.id ? 'true' : undefined}
                        onClick={() => setSelectedId(w.id)}
                      >
                        <span className="wsv2-row__name">{d.title.trim() || wdef?.label || w.widgetKey}</span>
                        {n > 0
                          ? <span className="wsv2-row__chg">바뀜 {n}</span>
                          : <span className="wsv2-row__span">{SPAN_LABEL[d.span] ?? d.span}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );

  const chrome = {
    title: draft.title.trim() || undefined,
    showTitle: draft.showTitle,
    height: draft.height,
    settings: draft.settings,
  };

  return (
    <Modal
      title="위젯 설정"
      onClose={close}
      size="xl"
      bodyFlat
      footer={(
        <>
          <ModalFootNote>
            <Button variant="ghost" size="sm" onClick={resetSelected}>이 위젯 기본값으로</Button>
            <span className="wsv2-where">이 브라우저에 저장됩니다</span>
          </ModalFootNote>
          <ModalActions>
            <Button variant="secondary" onClick={close}>취소</Button>
            <Button variant="primary" onClick={save} disabled={!dirty}>저장</Button>
          </ModalActions>
        </>
      )}
    >
      <div className="wsv2">
        <PickPreview
          list={list}
          listLabel="이 대시보드의 위젯"
          previewTitle="설정 뒤 모양"
          previewNote={draftChangeSummary(schema, defaultSpan, draft)}
          preview={def ? (
            <div className="wsv2-stage">
              <WidgetActionsContext.Provider value={INERT}>
                <WidgetChromeContext.Provider value={chrome}>
                  <def.Component />
                </WidgetChromeContext.Provider>
              </WidgetActionsContext.Provider>
            </div>
          ) : <p className="wsv2-hint">이 위젯은 더 이상 없습니다</p>}
        >
          <div className="wsv2-grid2">
            <div className="wsv2-field">
              <label className="wsv2-label" htmlFor={`${uid}-title`}>표시 이름</label>
              <Input
                id={`${uid}-title`}
                value={draft.title}
                maxLength={20}
                fullWidth
                placeholder={def?.label ?? ''}
                onChange={(e) => patch({ title: e.target.value })}
              />
            </div>
            <div className="wsv2-field">
              <span className="wsv2-label">제목</span>
              <Switch checked={draft.showTitle} onCheckedChange={(v) => patch({ showTitle: v })} label="제목 보이기" />
            </div>
          </div>

          <div className="wsv2-grid2">
            <div className="wsv2-field">
              <span className="wsv2-label" id={`${uid}-span`}>폭</span>
              <Segmented size="sm" labelledBy={`${uid}-span`} value={String(draft.span)} options={SPAN_OPTIONS}
                onChange={(v) => patch({ span: Number(v) as WidgetSpan })} />
            </div>
            <div className="wsv2-field">
              <span className="wsv2-label" id={`${uid}-height`}>높이</span>
              <Segmented size="sm" labelledBy={`${uid}-height`} value={draft.height} options={HEIGHT_OPTIONS}
                onChange={(v) => patch({ height: v as WidgetHeight })} />
            </div>
          </div>

          {schema && schema.groups.map((g) => {
            const resolved = resolveItems(schema, draft.settings, ctx, g.key);
            const onCount = resolved.filter((r) => r.on).length;
            const full = g.maxOn !== undefined && onCount >= g.maxOn;
            const reorderable = g.reorderable !== false;
            const caption = [
              g.label,
              g.maxOn !== undefined ? `최대 ${g.maxOn}개` : null,
              g.minOn !== undefined ? `${g.minOn}개는 켜 둡니다` : null,
              reorderable ? '손잡이로 순서를 바꿉니다' : null,
            ].filter(Boolean).join(' · ');
            return (
              <div key={g.key} className="wsv2-field">
                <span className="wsv2-label">{caption}</span>
                <ItemToggleList
                  ariaLabel={g.label}
                  reorderable={reorderable}
                  items={resolved.map((r) => ({
                    key: r.def.key,
                    label: r.def.label,
                    labelText: r.def.label,
                    description: r.def.desc,
                    on: r.on,
                    lockedReason: r.lock?.text ?? null,
                    cannotTurnOn: full,
                  }))}
                  onToggle={(key, next) => patchSettings(setItemOn(schema, draft.settings, key, next))}
                  onMove={(from, to) => patchSettings(moveItem(schema, draft.settings, g.key, from, to))}
                />
              </div>
            );
          })}

          {schema && schema.options.map((o) => (
            <OptionField
              key={o.key}
              schema={schema}
              option={o}
              settings={draft.settings}
              ctx={ctx}
              onChange={(v) => patchSettings(setOption(draft.settings, o.key, v))}
            />
          ))}

          {/* v2 V4 — 빠른 실행은 켜기 · 순서를 함께 고친다(옛 창의 체크박스 고르개 대신). */}
          {selected.widgetKey === 'quick-launch' && (
            <QuickLinkOrderField value={draft.settings} onChange={(p) => patchSettings(p)} />
          )}
          {hasOwnSettings(selected.widgetKey) && selected.widgetKey !== 'quick-launch' && (
            <div className="wsv2-field">
              <span className="wsv2-label">이 위젯만의 설정</span>
              <WidgetOwnSettings widgetKey={selected.widgetKey} value={draft.settings} onChange={(p) => patchSettings(p)} />
            </div>
          )}
        </PickPreview>
      </div>
    </Modal>
  );
}

function OptionField({
  schema, option, settings, ctx, onChange,
}: {
  schema: WidgetItemSchema;
  option: OptionDef;
  settings: WidgetSettings;
  ctx: ItemContext;
  onChange: (value: WidgetOptionValue) => void;
}) {
  const id = useId();
  const value = resolveOption(schema, settings, option.key, ctx);

  if (option.kind === 'toggle') {
    return (
      <div className="wsv2-field">
        <Switch checked={value === true} onCheckedChange={(v) => onChange(v)} label={option.label} />
        {option.desc && <p className="wsv2-hint">{option.desc}</p>}
      </div>
    );
  }

  const choices = resolveChoices(option, ctx);
  const lockHint = choices.find((c) => c.lock)?.lock?.text;

  if (option.kind === 'single') {
    return (
      <div className="wsv2-field">
        <span className="wsv2-label" id={id}>{option.label}</span>
        <Segmented
          size="sm"
          labelledBy={id}
          value={String(value)}
          onChange={onChange}
          options={choices.map((c) => ({
            value: c.def.value, label: c.def.label, disabled: Boolean(c.lock), disabledReason: c.lock?.text,
          }))}
          hint={option.hint ?? lockHint}
        />
      </div>
    );
  }

  const arr = Array.isArray(value) ? value : [];
  return (
    <fieldset className="wsv2-field wsv2-multi">
      <legend className="wsv2-label">{option.label}</legend>
      <div className="wsv2-checks">
        {choices.map((c) => (
          <Checkbox
            key={c.def.value}
            label={c.def.label}
            checked={arr.includes(c.def.value)}
            disabled={Boolean(c.lock)}
            onChange={(e) => {
              const next = e.target.checked ? [...arr, c.def.value] : arr.filter((v) => v !== c.def.value);
              // 정의 순서로 굳힌다 — 누른 순서에 따라 저장값이 달라지지 않게.
              onChange(option.choices.map((x) => x.value).filter((v) => next.includes(v)));
            }}
          />
        ))}
      </div>
      {(option.hint ?? lockHint) && <p className="wsv2-hint">{option.hint ?? lockHint}</p>}
    </fieldset>
  );
}
